import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronDown,
  BarChart2,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Check,
  RotateCw,
  AlertTriangle,
  ArrowRight,
  Activity,
  Cpu,
  Layers,
  Radio,
  Clock,
  FlaskConical,
} from "lucide-react";
import type {
  TimeRangeKey,
  MetricSummary,
  ChartBucket,
  RequestRecord,
} from "../../types/telemetry.ts";
import { formatDateTimeIST, formatBucketTooltipIST } from "../../utils/dateTime.ts";
import { Skeleton } from "../common/Skeleton.tsx";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { RequestDetailPanel } from "./RequestDetailPanel.tsx";
import { ResearchReportView } from "./ResearchReportView.tsx";

export interface OverviewViewProps {
  summary: MetricSummary | null;
  chart: { bucketSeconds: number; buckets: ChartBucket[] } | null;
  activeTimeRange: TimeRangeKey;
  onSelectTimeRange: (range: TimeRangeKey) => void;
  isLoading?: boolean;
  isRefreshing?: boolean;
  isStale?: boolean;
  serverTime?: string | null;
  error?: Error | null;
  onRefresh?: () => Promise<boolean | void>;
  isSyntheticMode?: boolean;

  // Selected website identity for resetting internal report view on website switch
  websiteId?: string;

  // Recent security events and drawer integration
  recentSecurityEvents?: RequestRecord[];
  selectedRequestId?: string | null;
  onSelectRequestId?: (id: string | null) => void;
  detailRequest?: RequestRecord | null;
  detailLoading?: boolean;
  detailError?: Error | null;
  onDetailRetry?: () => void;
}

const TIME_RANGE_OPTIONS: { key: TimeRangeKey; label: string }[] = [
  { key: "15m", label: "Last 15 minutes" },
  { key: "60m", label: "Last 60 minutes" },
  { key: "24h", label: "Last 24 hours" },
  { key: "all", label: "All records" },
];

type RefreshUiStatus = "idle" | "pending" | "success" | "failure";

/** Render a metric value, showing "—" during loading, error, or when value is null */
const MetricValue: React.FC<{
  value: string | number | null;
  isLoading: boolean;
  className?: string;
}> = ({ value, isLoading, className = "" }) => {
  if (isLoading && value === null) {
    return <Skeleton className="h-8 w-20 inline-block rounded my-0.5" />;
  }
  const display = value === null ? "—" : String(value);
  return (
    <span className={`text-2xl font-semibold text-foreground tabular-nums tracking-tight ${className}`}>
      {display}
    </span>
  );
};

export const OverviewView: React.FC<OverviewViewProps> = ({
  summary,
  chart,
  activeTimeRange,
  onSelectTimeRange,
  isLoading = false,
  isRefreshing = false,
  isStale = false,
  serverTime = null,
  error = null,
  onRefresh,
  isSyntheticMode = false,
  websiteId,
  recentSecurityEvents = [],
  selectedRequestId = null,
  onSelectRequestId,
  detailRequest = null,
  detailLoading = false,
  detailError = null,
  onDetailRetry,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredBucket, setHoveredBucket] = useState<string | null>(null);

  // Dedicated research comparison report view state
  const [showResearchReport, setShowResearchReport] = useState<boolean>(false);

  // Reset research report view whenever selected website changes
  useEffect(() => {
    setShowResearchReport(false);
  }, [websiteId]);

  // Manual refresh interaction state machine
  const [refreshStatus, setRefreshStatus] = useState<RefreshUiStatus>("idle");
  const [rippleKey, setRippleKey] = useState<number>(0);
  const [announcement, setAnnouncement] = useState<string>("");

  const isMountedRef = useRef<boolean>(true);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentRangeRef = useRef<TimeRangeKey>(activeTimeRange);
  const requestGenerationRef = useRef<number>(0);

  // Sync current range and invalidate any in-flight refresh feedback on range change
  useEffect(() => {
    currentRangeRef.current = activeTimeRange;
    requestGenerationRef.current += 1;
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    setRefreshStatus("idle");
  }, [activeTimeRange]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      requestGenerationRef.current += 1;
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, []);

  const handleRefreshClick = useCallback(async () => {
    if (refreshStatus === "pending" || isRefreshing || !onRefresh) {
      return;
    }

    const thisGeneration = ++requestGenerationRef.current;
    const targetRange = currentRangeRef.current;

    setRefreshStatus("pending");
    setAnnouncement("Refreshing telemetry data…");
    setRippleKey((prev) => prev + 1);

    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }

    try {
      const outcome = await onRefresh();

      // Invalidate obsolete completions if range changed or component unmounted
      if (
        !isMountedRef.current ||
        requestGenerationRef.current !== thisGeneration ||
        currentRangeRef.current !== targetRange
      ) {
        return;
      }

      // Explicit success check: require outcome === true
      if (outcome !== true) {
        setRefreshStatus("failure");
        setAnnouncement("Refresh failed. Telemetry data may be stale.");
        return;
      }

      setRefreshStatus("success");
      setAnnouncement(
        isSyntheticMode ? "Demo refreshed" : "Telemetry data updated."
      );

      resetTimerRef.current = setTimeout(() => {
        if (
          isMountedRef.current &&
          requestGenerationRef.current === thisGeneration &&
          currentRangeRef.current === targetRange
        ) {
          setRefreshStatus("idle");
        }
      }, 2000);
    } catch {
      if (
        !isMountedRef.current ||
        requestGenerationRef.current !== thisGeneration ||
        currentRangeRef.current !== targetRange
      ) {
        return;
      }
      setRefreshStatus("failure");
      setAnnouncement("Refresh failed. Telemetry data may be stale.");
    }
  }, [refreshStatus, isRefreshing, onRefresh, isSyntheticMode]);

  const buckets: ChartBucket[] = chart?.buckets ?? [];
  const maxBucketTotal = Math.max(1, ...buckets.map((b: ChartBucket) => b.total));

  const activeLabel =
    TIME_RANGE_OPTIONS.find((opt) => opt.key === activeTimeRange)?.label || "Last 60 minutes";

  const isPending = refreshStatus === "pending" || isRefreshing;
  const isInitialLoading = isLoading && !summary;

  // Up to 5 actual bundle records for recent security events
  const displayRecentEvents = recentSecurityEvents.slice(0, 5);

  // Dedicated research comparison report view
  if (showResearchReport) {
    return (
      <ResearchReportView
        onBack={() => setShowResearchReport(false)}
        isSyntheticMode={isSyntheticMode}
      />
    );
  }

  // Initial outage / changed-query failure where no data exists
  if (error && !summary) {
    return (
      <div className="flex-grow p-6 lg:p-8 max-w-7xl mx-auto w-full select-none flex flex-col items-center justify-center min-h-[400px]">
        <div className="p-8 rounded-lg bg-panel border border-border max-w-md w-full text-center space-y-3">
          <ShieldAlert className="w-8 h-8 text-verdict-block mx-auto" />
          <h2 className="text-base font-medium text-foreground">Unable to load telemetry metrics</h2>
          <p className="text-xs text-foreground-muted">
            {error.message || "An unexpected error occurred while communicating with the management API."}
          </p>
          {onRefresh && (
            <button
              type="button"
              onClick={handleRefreshClick}
              disabled={isPending}
              className="px-3.5 py-1.5 rounded-md bg-brand text-white text-xs font-medium hover:bg-brand-hover transition-colors inline-flex items-center gap-1.5"
            >
              <RotateCw className={`w-3.5 h-3.5 ${isPending ? (prefersReducedMotion ? "" : "animate-spin") : ""}`} />
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-busy={isInitialLoading}
      aria-label="Overview telemetry"
      className="flex-grow p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full select-none"
    >
      {isInitialLoading && (
        <span className="sr-only">Loading overview telemetry metrics</span>
      )}

      {/* Screen reader announcement for manual refresh feedback */}
      <div
        className="sr-only"
        role="status"
        aria-live={refreshStatus === "failure" ? "assertive" : "polite"}
      >
        {announcement}
      </div>

      {/* 1. Header: Title, Window, Refresh Button and Last Successful Update in IST */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-medium text-foreground tracking-tight">
              Overview
            </h1>
            {isSyntheticMode && (
              <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30">
                Synthetic Demo
              </span>
            )}
          </div>
          <p className="text-sm text-foreground-muted mt-1">
            Traffic inspection, rule-based policies, and neural security evaluation
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 self-start sm:self-auto">
          {/* Time Window Dropdown Picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
              aria-label="Select telemetry time window"
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-panel border border-border text-xs font-medium text-foreground hover:bg-panel-hover transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
            >
              <Clock className="w-3.5 h-3.5 text-foreground-muted" />
              <span>{activeLabel}</span>
              <ChevronDown className="w-3.5 h-3.5 text-foreground-muted" />
            </button>

            {isDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setIsDropdownOpen(false)}
                  aria-hidden="true"
                />
                <div
                  role="listbox"
                  className="absolute right-0 mt-1 w-44 rounded-md bg-panel border border-border shadow-xl z-30 py-1"
                >
                  {TIME_RANGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      role="option"
                      aria-selected={activeTimeRange === opt.key}
                      onClick={() => {
                        onSelectTimeRange(opt.key);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left transition-colors ${
                        activeTimeRange === opt.key
                          ? "bg-panel-hover text-foreground font-medium"
                          : "text-foreground-muted hover:text-foreground hover:bg-panel-hover/60"
                      }`}
                    >
                      <span>{opt.label}</span>
                      {activeTimeRange === opt.key && (
                        <Check className="w-3.5 h-3.5 text-brand" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Last updated indicator */}
          <div
            className="text-xs text-foreground-muted select-none flex items-center gap-1.5 px-2 py-1 rounded bg-panel/60 border border-border-subtle"
            data-testid="last-updated"
          >
            <span>Last updated:</span>
            <span className="text-foreground font-mono tabular-nums">
              {formatDateTimeIST(serverTime)}
            </span>
          </div>

          {/* Polished Expressive Refresh Button */}
          {onRefresh && (
            <motion.button
              type="button"
              onClick={handleRefreshClick}
              disabled={isPending}
              aria-busy={isPending}
              aria-label={
                refreshStatus === "pending"
                  ? "Refreshing telemetry data"
                  : refreshStatus === "success"
                  ? isSyntheticMode
                    ? "Demo refreshed"
                    : "Telemetry data updated"
                  : refreshStatus === "failure"
                  ? "Refresh failed, retry"
                  : "Refresh telemetry bundle"
              }
              whileTap={
                !prefersReducedMotion && !isPending
                  ? { scale: 0.94 }
                  : undefined
              }
              transition={{ type: "spring", stiffness: 500, damping: 25 }}
              className={`relative overflow-hidden px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none transition-colors select-none ${
                refreshStatus === "pending"
                  ? "bg-brand/10 border-brand/60 text-brand cursor-wait shadow-[0_0_12px_rgba(59,130,246,0.25)]"
                  : refreshStatus === "success"
                  ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-300 cursor-default"
                  : refreshStatus === "failure"
                  ? "bg-amber-500/10 border-amber-500/50 text-amber-300 hover:bg-amber-500/20 cursor-pointer"
                  : "bg-panel border-border text-foreground hover:bg-panel-hover hover:border-border-muted cursor-pointer"
              }`}
              title="Refresh telemetry bundle"
            >
              {/* Confined blue ripple effect */}
              {!prefersReducedMotion && (
                <AnimatePresence>
                  {rippleKey > 0 && (
                    <motion.span
                      key={rippleKey}
                      initial={{ scale: 0, opacity: 0.45 }}
                      animate={{ scale: 3.2, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                      className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-brand/30 pointer-events-none"
                    />
                  )}
                </AnimatePresence>
              )}

              {/* Icon & Label */}
              {refreshStatus === "pending" ? (
                <>
                  {prefersReducedMotion ? (
                    <RotateCw className="w-3.5 h-3.5 text-brand shrink-0" />
                  ) : (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        repeat: Infinity,
                        duration: 0.85,
                        ease: "linear",
                      }}
                      className="shrink-0 flex items-center justify-center"
                    >
                      <RotateCw className="w-3.5 h-3.5 text-brand" />
                    </motion.div>
                  )}
                  <span className="font-medium text-brand">Refreshing…</span>
                </>
              ) : refreshStatus === "success" ? (
                <>
                  {prefersReducedMotion ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  ) : (
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 450, damping: 22 }}
                      className="shrink-0 flex items-center justify-center"
                    >
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    </motion.div>
                  )}
                  <span className="font-medium text-emerald-300">
                    {isSyntheticMode ? "Demo refreshed" : "Updated"}
                  </span>
                </>
              ) : refreshStatus === "failure" ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span className="font-medium text-amber-300">Retry</span>
                </>
              ) : (
                <>
                  <RotateCw
                    className={`w-3.5 h-3.5 shrink-0 ${
                      isRefreshing
                        ? prefersReducedMotion
                          ? "text-brand"
                          : "animate-spin text-brand"
                        : "text-foreground-muted"
                    }`}
                  />
                  <span>{isRefreshing ? "Refreshing…" : "Refresh"}</span>
                </>
              )}
            </motion.button>
          )}

          {/* Secondary "More details" button for Research Evaluation */}
          <button
            type="button"
            onClick={() => setShowResearchReport(true)}
            aria-label="View research evaluation report"
            data-testid="overview-more-details-btn"
            className="px-3 py-1.5 rounded-md bg-panel border border-border text-xs font-medium text-foreground hover:bg-panel-hover hover:border-border-muted transition-colors flex items-center gap-1.5 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none select-none cursor-pointer"
          >
            <FlaskConical className="w-3.5 h-3.5 text-brand shrink-0" />
            <span>More details</span>
          </button>
        </div>
      </div>

      {/* Stale Cache Notice Banner */}
      {isStale && (
        <div
          data-testid="stale-notice"
          className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-between text-xs text-amber-200"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <span>
              Telemetry data may be stale
              {serverTime ? ` (cached from ${formatDateTimeIST(serverTime)})` : ""}.
              A background refresh failed.
            </span>
          </div>
          {onRefresh && (
            <button
              type="button"
              onClick={handleRefreshClick}
              disabled={isPending}
              className="px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 font-medium transition-colors flex items-center gap-1.5"
            >
              <RotateCw className={`w-3 h-3 ${isPending ? (prefersReducedMotion ? "" : "animate-spin") : ""}`} />
              Retry
            </button>
          )}
        </div>
      )}

      {/* 2. Summary Cards: Total Requests, Blocked Requests/Rate, Flagged Requests, AI-Evaluated Requests/Coverage */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Requests */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-2">
            Total Requests
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <MetricValue
              value={isInitialLoading ? null : (summary?.totalRequests ?? 0)}
              isLoading={isInitialLoading}
            />
          </div>
          <div className="text-xs text-foreground-dim mt-2">
            Full-window incoming traffic
          </div>
        </div>

        {/* Card 2: Blocked Requests & Rate */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-2">
            Blocked Requests
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <MetricValue
              value={isInitialLoading ? null : (summary?.blockCount ?? 0)}
              isLoading={isInitialLoading}
              className="text-verdict-block"
            />
            <span className="text-xs font-medium font-mono text-verdict-block px-1.5 py-0.5 rounded bg-verdict-block-bg">
              {isInitialLoading || summary?.blockRate === null
                ? "—"
                : `${summary!.blockRate.toFixed(1)}%`}
            </span>
          </div>
          <div className="text-xs text-foreground-dim mt-2">
            Policy blocks across window
          </div>
        </div>

        {/* Card 3: Flagged Requests */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-2">
            Flagged Requests
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <MetricValue
              value={isInitialLoading ? null : (summary?.flagCount ?? 0)}
              isLoading={isInitialLoading}
              className="text-verdict-flag"
            />
            <span className="text-xs font-medium font-mono text-verdict-flag px-1.5 py-0.5 rounded bg-verdict-flag-bg">
              {isInitialLoading || !summary || summary.totalRequests === 0
                ? "—"
                : `${((summary.flagCount / summary.totalRequests) * 100).toFixed(1)}%`}
            </span>
          </div>
          <div className="text-xs text-foreground-dim mt-2">
            Monitored policy flags
          </div>
        </div>

        {/* Card 4: AI-Evaluated Requests & Coverage */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-2">
            AI-Evaluated Requests
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <MetricValue
              value={isInitialLoading ? null : (summary?.aiEvaluatedCount ?? 0)}
              isLoading={isInitialLoading}
              className="text-brand"
            />
            <span className="text-xs font-medium font-mono text-brand px-1.5 py-0.5 rounded bg-brand/10">
              {isInitialLoading || summary?.aiCoveragePercent === null || summary?.aiCoveragePercent === undefined
                ? "—"
                : `${summary.aiCoveragePercent.toFixed(1)}%`}
            </span>
          </div>
          <div className="text-xs text-foreground-dim mt-2">
            AI evaluation coverage
          </div>
        </div>
      </div>

      {/* 3. Main Traffic Chart: Retain genuine time buckets and clear verdict legend */}
      <div className="p-5 rounded-lg bg-panel border border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-sm font-medium text-foreground">
              Traffic Volume & Verdicts
            </h2>
            <p className="text-xs text-foreground-muted mt-0.5">
              Mutually exclusive policy decisions over genuine time buckets
            </p>
          </div>

          {/* Verdict Legend */}
          <div className="flex flex-wrap items-center gap-3.5 text-xs text-foreground-dim">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-verdict-allow" />
              <span>Allow ({summary?.allowCount ?? 0})</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-verdict-flag" />
              <span>Flag ({summary?.flagCount ?? 0})</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-verdict-block" />
              <span>Block ({summary?.blockCount ?? 0})</span>
            </span>
            {(summary?.notEvaluatedCount ?? 0) > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-verdict-neutral" />
                <span>Not Evaluated ({summary?.notEvaluatedCount ?? 0})</span>
              </span>
            )}
          </div>
        </div>

        {/* Traffic Histogram Stacked by Mutually Exclusive Decisions */}
        <div className="h-44 rounded border border-border-subtle bg-canvas/40 p-3 flex flex-col justify-end relative">
          {isInitialLoading ? (
            <div className="flex items-end justify-between gap-1 h-32 w-full pt-2" aria-hidden="true">
              {[35, 60, 25, 80, 45, 65, 40, 75, 30, 85, 50, 70, 45, 60, 80, 55].map((h, i) => (
                <div key={i} className="flex-1 min-w-0 flex flex-col justify-end h-full">
                  <Skeleton style={{ height: `${h}%` }} className="w-full rounded-t-sm" />
                </div>
              ))}
            </div>
          ) : !summary || summary.totalRequests === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-foreground-dim gap-2">
              <BarChart2 className="w-6 h-6 opacity-40" />
              <span className="text-xs">No traffic in this window</span>
            </div>
          ) : (
            <div className={`flex items-end justify-between ${buckets.length > 40 ? "gap-[1px]" : "gap-1"} h-32 w-full pt-2`}>
              {buckets.map((b: ChartBucket) => {
                const barHeightPct = Math.max(6, Math.round((b.total / maxBucketTotal) * 100));
                const isHovered = hoveredBucket === b.bucketKey;

                return (
                  <div
                    key={b.bucketKey}
                    onMouseEnter={() => setHoveredBucket(b.bucketKey)}
                    onMouseLeave={() => setHoveredBucket(null)}
                    className="flex-1 min-w-0 flex flex-col justify-end h-full relative cursor-pointer group"
                  >
                    {/* Tooltip */}
                    {isHovered && b.total > 0 && (
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-panel border border-border text-[11px] rounded px-2.5 py-1.5 whitespace-nowrap shadow-xl z-30 pointer-events-none space-y-0.5">
                        <div className="font-mono text-foreground font-semibold pb-1 border-b border-border-subtle">
                          {formatBucketTooltipIST(b.startTime)}
                        </div>
                        <div className="text-verdict-allow flex justify-between gap-3">
                          <span>Allow:</span>
                          <span className="font-mono font-medium">{b.allow}</span>
                        </div>
                        <div className="text-verdict-flag flex justify-between gap-3">
                          <span>Flag:</span>
                          <span className="font-mono font-medium">{b.flag}</span>
                        </div>
                        <div className="text-verdict-block flex justify-between gap-3">
                          <span>Block:</span>
                          <span className="font-mono font-medium">{b.block}</span>
                        </div>
                        {b.notEvaluated > 0 && (
                          <div className="text-verdict-neutral flex justify-between gap-3">
                            <span>Not evaluated:</span>
                            <span className="font-mono font-medium">{b.notEvaluated}</span>
                          </div>
                        )}
                        <div className="text-foreground-dim pt-1 border-t border-border-subtle flex justify-between gap-3">
                          <span>Total:</span>
                          <span className="font-mono font-medium text-foreground">{b.total}</span>
                        </div>
                      </div>
                    )}

                    {/* Stacked Bar Segments */}
                    <div
                      style={{ height: `${barHeightPct}%` }}
                      className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden bg-panel-subtle"
                    >
                      {b.allow > 0 && (
                        <div
                          style={{ height: `${(b.allow / b.total) * 100}%` }}
                          className="w-full bg-verdict-allow"
                        />
                      )}
                      {b.flag > 0 && (
                        <div
                          style={{ height: `${(b.flag / b.total) * 100}%` }}
                          className="w-full bg-verdict-flag"
                        />
                      )}
                      {b.block > 0 && (
                        <div
                          style={{ height: `${(b.block / b.total) * 100}%` }}
                          className="w-full bg-verdict-block"
                        />
                      )}
                      {b.notEvaluated > 0 && (
                        <div
                          style={{ height: `${(b.notEvaluated / b.total) * 100}%` }}
                          className="w-full bg-verdict-neutral"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 4. Four Analytical Panels: AI Evaluation, Latencies, Decision Attribution, Recorded Modes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Panel A: AI Evaluation Panel */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="w-3.5 h-3.5 text-brand" />
              <h3 className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
                AI Evaluation
              </h3>
            </div>
            <p className="text-[11px] text-foreground-dim">
              Neural model pipeline breakdown
            </p>
          </div>

          <div className="mt-3 rounded border border-border-subtle bg-canvas/40 p-3 space-y-2 text-xs">
            {isInitialLoading ? (
              <div className="space-y-2" aria-hidden="true">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-8" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Evaluated (OK)</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.aiEvaluatedCount ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Skipped</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.aiSkippedCount ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Not Evaluated</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.aiNotEvaluatedCount ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Unreliable Redaction</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.aiUnreliableRedactionCount ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Operational Failures</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.aiFailureCount ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Other Statuses</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.aiOtherStatusCount ?? 0}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Panel B: Latency Panel */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-3.5 h-3.5 text-brand" />
              <h3 className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
                Latency
              </h3>
            </div>
            <p className="text-[11px] text-foreground-dim">
              Transit, origin, and neural round-trip
            </p>
          </div>

          <div className="mt-3 rounded border border-border-subtle bg-canvas/40 p-3 space-y-2 text-xs">
            {isInitialLoading ? (
              <div className="space-y-2" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-muted">Gateway Transit</span>
                    <span className="font-mono text-foreground font-medium">
                      {summary?.avgTransitMs !== null && summary?.avgTransitMs !== undefined
                        ? `${summary.avgTransitMs.toFixed(1)} ms`
                        : "—"}
                    </span>
                  </div>
                  <div className="text-[10px] text-foreground-dim mt-0.5">
                    Client receipt to response completion
                  </div>
                </div>

                <div className="pt-1.5 border-t border-border-subtle">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-muted">Upstream Headers</span>
                    <span className="font-mono text-foreground font-medium">
                      {summary?.avgUpstreamHeaderMs !== null && summary?.avgUpstreamHeaderMs !== undefined
                        ? `${summary.avgUpstreamHeaderMs.toFixed(1)} ms`
                        : "—"}
                    </span>
                  </div>
                  <div className="text-[10px] text-foreground-dim mt-0.5">
                    Time to origin header (excludes blocked)
                  </div>
                </div>

                <div className="pt-1.5 border-t border-border-subtle">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-muted">AI Round-Trip</span>
                    <span className="font-mono text-foreground font-medium">
                      {summary?.avgAiRoundtripMs !== null && summary?.avgAiRoundtripMs !== undefined
                        ? `${summary.avgAiRoundtripMs.toFixed(1)} ms`
                        : "—"}
                    </span>
                  </div>
                  <div className="text-[10px] text-foreground-dim mt-0.5">
                    Evaluated neural inference round-trip
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Panel C: Decision Attribution (Labelled: Across all verdicts) */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-3.5 h-3.5 text-brand" />
              <h3 className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
                Decision Attribution
              </h3>
            </div>
            <p className="text-[11px] text-foreground-dim">
              Across all verdicts
            </p>
          </div>

          <div className="mt-3 rounded border border-border-subtle bg-canvas/40 p-3 space-y-2 text-xs">
            {isInitialLoading ? (
              <div className="space-y-2" aria-hidden="true">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-8" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">CRS Rules</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.decisionAttribution?.crs ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">AI Neural</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.decisionAttribution?.ai ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">BOTH</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.decisionAttribution?.both ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Unavailable / None</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.decisionAttribution?.unknown ?? 0}
                  </span>
                </div>
                <div className="text-[10px] text-foreground-dim pt-1 border-t border-border-subtle">
                  Attribution across all verdicts in window
                </div>
              </>
            )}
          </div>
        </div>

        {/* Panel D: Recorded Modes (Explicitly labelled: Historical request modes — not current configuration) */}
        <div className="p-4 rounded-lg bg-panel border border-border flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Radio className="w-3.5 h-3.5 text-brand" />
              <h3 className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
                Recorded Modes
              </h3>
            </div>
            <p className="text-[10px] text-foreground-dim leading-snug">
              Historical request modes — not current configuration
            </p>
          </div>

          <div className="mt-3 rounded border border-border-subtle bg-canvas/40 p-3 space-y-2 text-xs">
            {isInitialLoading ? (
              <div className="space-y-2" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-8" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">SHADOW</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.recordedModes?.shadow ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">HYBRID</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.recordedModes?.hybrid ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Other / Unset</span>
                  <span className="font-mono text-foreground font-medium">
                    {summary?.recordedModes?.other ?? 0}
                  </span>
                </div>
                <div className="text-[10px] text-foreground-dim pt-1 border-t border-border-subtle">
                  Reflects mode recorded at request time
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 5. Recent Security Events: Up to five actual bundle records */}
      <div className="p-5 rounded-lg bg-panel border border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-medium text-foreground">
              Recent Security Events
            </h2>
            <p className="text-xs text-foreground-muted mt-0.5">
              Up to 5 recent flagged, blocked, or technical gateway failure records from the current window
            </p>
          </div>
          {displayRecentEvents.length > 0 && (
            <span className="text-xs text-foreground-dim font-mono">
              Showing {displayRecentEvents.length} event{displayRecentEvents.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {isInitialLoading ? (
          <div className="space-y-2" aria-hidden="true">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-3 rounded border border-border-subtle bg-canvas/30 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-44" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : displayRecentEvents.length === 0 ? (
          <div className="p-8 rounded border border-border-subtle bg-canvas/20 flex flex-col items-center justify-center text-center">
            <ShieldCheck className="w-8 h-8 text-verdict-allow mb-2 opacity-80" />
            <p className="text-xs font-medium text-foreground">
              No security events recorded in this time window
            </p>
            <p className="text-[11px] text-foreground-muted mt-1 max-w-sm">
              All requests in this window passed without policy blocks, flags, or gateway inspector failures.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-border text-foreground-muted font-medium">
                  <th className="py-2.5 px-3">Time (IST)</th>
                  <th className="py-2.5 px-3">Method & Path</th>
                  <th className="py-2.5 px-3">Verdict</th>
                  <th className="py-2.5 px-3">Source</th>
                  <th className="py-2.5 px-3">Outcome</th>
                  <th className="py-2.5 px-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {displayRecentEvents.map((event) => {
                  const isTechFailure =
                    event.gateway_outcome === "GATEWAY_ERROR" ||
                    event.response_status === 502;
                  const isBlock =
                    event.policy_decision === "BLOCK" ||
                    event.gateway_outcome === "BLOCKED";
                  const isFlag = event.policy_decision === "FLAG";

                  return (
                    <tr
                      key={event.id}
                      onClick={() => onSelectRequestId?.(event.id)}
                      data-testid={`recent-event-row-${event.id}`}
                      className="hover:bg-panel-hover/60 cursor-pointer transition-colors group"
                    >
                      {/* IST Timestamp */}
                      <td className="py-2.5 px-3 font-mono text-foreground-dim whitespace-nowrap">
                        {formatDateTimeIST(event.timestamp)}
                      </td>

                      {/* Method & Path */}
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2 max-w-xs md:max-w-sm truncate">
                          <span className="font-mono font-medium text-foreground px-1 py-0.5 rounded bg-canvas border border-border-subtle text-[11px]">
                            {event.method}
                          </span>
                          <span className="font-mono text-foreground truncate" title={event.raw_path}>
                            {event.raw_path}
                          </span>
                        </div>
                      </td>

                      {/* Final Verdict */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                            isBlock
                              ? "bg-verdict-block-bg text-verdict-block border border-verdict-block/20"
                              : isFlag
                              ? "bg-verdict-flag-bg text-verdict-flag border border-verdict-flag/20"
                              : "bg-verdict-allow-bg text-verdict-allow border border-verdict-allow/20"
                          }`}
                        >
                          {event.policy_decision ?? "—"}
                        </span>
                      </td>

                      {/* Decision Source */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span className="font-mono text-[11px] text-foreground-dim">
                          {event.decision_source ?? "—"}
                        </span>
                      </td>

                      {/* Outcome: Distinguish attack policy block from technical GATEWAY_ERROR / 502 */}
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        {isTechFailure ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30">
                            <AlertTriangle className="w-3 h-3 text-amber-400" />
                            Technical 502
                          </span>
                        ) : isBlock ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-verdict-block-bg text-verdict-block">
                            <ShieldX className="w-3 h-3 text-verdict-block" />
                            Policy Blocked
                          </span>
                        ) : isFlag ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-verdict-flag-bg text-verdict-flag">
                            <ShieldAlert className="w-3 h-3 text-verdict-flag" />
                            Policy Flagged
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-foreground-dim">
                            {event.gateway_outcome}
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="py-2.5 px-3 text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-[11px] text-brand opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                          Inspect
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 6. System Status Panel: Accurately scoped to management API & database */}
      <div className="p-4 rounded-lg bg-panel border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2.5">
          {isSyntheticMode ? (
            <>
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <div>
                <span className="font-medium text-foreground">
                  Synthetic test mode active (?mode=synthetic)
                </span>
                <p className="text-[11px] text-foreground-muted mt-0.5">
                  Client fixtures anchored to reference timestamp 2026-10-04 15:30:00Z (04 Oct 2026, 21:00:00 IST). Zero server calls.
                </p>
              </div>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <div>
                <span className="font-medium text-foreground">
                  Management API & Telemetry Log Active
                </span>
                <p className="text-[11px] text-foreground-muted mt-0.5">
                  Reading PostgreSQL telemetry database. Aggregate window calculations computed server-side.
                </p>
              </div>
            </>
          )}
        </div>

        {serverTime && (
          <div className="text-foreground-dim text-[11px] font-mono whitespace-nowrap">
            Server: {formatDateTimeIST(serverTime)}
          </div>
        )}
      </div>

      {/* 7. Selected Request Detail Drawer */}
      <RequestDetailPanel
        isOpen={selectedRequestId !== null && selectedRequestId !== undefined}
        requestId={selectedRequestId ?? null}
        request={detailRequest ?? null}
        isLoading={Boolean(detailLoading)}
        error={detailError ?? null}
        onRetry={onDetailRetry}
        onClose={() => onSelectRequestId?.(null)}
        isSyntheticMode={isSyntheticMode}
      />
    </div>
  );
};
